import {reportSaveFailed, reportSaved, reportSaving} from '../lib/saveStatus.js';

export type RecordAutosaveState = 'idle' | 'pending' | 'saving' | 'failed';

export type RecordAutosaveOptions<T extends Record<string, unknown>> = {
  key: string;
  initial: T;
  write: (value: T) => Promise<unknown>;
  onDraft: (value: T) => void;
  onState: (state: RecordAutosaveState) => void;
  delayMs?: number;
};

/** Page-local record draft owner. Writes are serial, and a failure or refetch
 * never replaces the newest local draft. Retry and navigation flush both write
 * the current value, including edits made during an earlier request. */
export function createRecordAutosave<T extends Record<string, unknown>>(options: RecordAutosaveOptions<T>) {
  let draft = {...options.initial};
  let persisted = {...options.initial};
  let revision = 0;
  let savedRevision = 0;
  let running: Promise<boolean> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  let protectedValues: Partial<T> = {};

  const delay = options.delayMs ?? 350;
  const setState = (state: RecordAutosaveState) => {
    if (!disposed) options.onState(state);
  };

  function isDirty() {
    return revision !== savedRevision;
  }

  function mergeServer(server: T) {
    const merged = {...server};
    for (const key of Object.keys(draft) as (keyof T)[]) {
      const localIsDirty = isDirty() && !Object.is(draft[key], persisted[key]);
      const protectedValue = protectedValues[key];
      if (localIsDirty || (protectedValue !== undefined && !Object.is(server[key], protectedValue))) {
        merged[key] = draft[key];
      } else {
        if (protectedValue !== undefined && Object.is(server[key], protectedValue)) delete protectedValues[key];
        draft[key] = server[key];
        persisted[key] = server[key];
      }
    }
    options.onDraft(merged);
    return merged;
  }

  function update(patch: Partial<T>) {
    draft = {...draft, ...patch};
    revision += 1;
    options.onDraft(draft);
    setState('pending');
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void flush(), delay);
  }

  async function drain(): Promise<boolean> {
    if (running) return running;
    const work = (async () => {
      while (!disposed && isDirty()) {
        const writeRevision = revision;
        const value = {...draft};
        setState('saving');
        const attempt = reportSaving(options.key);
        try {
          await options.write(value);
          if (disposed) return false;
          persisted = {...value};
          for (const key of Object.keys(value) as (keyof T)[]) protectedValues[key] = value[key];
          if (revision === writeRevision) {
            savedRevision = writeRevision;
            reportSaved(options.key, attempt);
            setState('idle');
          }
        } catch {
          if (disposed) return false;
          if (revision !== writeRevision) continue;
          reportSaveFailed(options.key, () => void retry(), attempt);
          setState('failed');
          return false;
        }
      }
      return !isDirty();
    })();
    running = work;
    try {
      return await work;
    } finally {
      if (running === work) running = null;
    }
  }

  async function flush(): Promise<boolean> {
    if (timer) clearTimeout(timer);
    timer = null;
    return drain();
  }

  async function retry(): Promise<boolean> {
    setState('pending');
    return flush();
  }

  function dispose() {
    disposed = true;
    if (timer) clearTimeout(timer);
    timer = null;
  }

  return {
    update,
    mergeServer,
    flush,
    retry,
    dispose,
    isDirty,
    getDraft: () => draft,
    getPersisted: () => persisted,
  };
}
